import { CommonModule } from '@angular/common';
import {
  AfterViewChecked,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { UiButtonDirective, UiControlDirective } from '@template-ui/directives';
import type {
  NoticeComposition,
  NoticeDiagnostic,
  NoticeProof,
  SavedNoticeSource,
} from './notice-fidelity.models';
import type { NoticeCompositionComparison } from './notice-composition-diff';
import {
  computeNoticeProofScale,
  NoticeProofViewportDirective,
  type NoticeProofViewport,
  type NoticeProofZoom,
} from './notice-proof-viewport';

type ProofSide = 'baseline' | 'current';
type ProofPair = { baseline: NoticeProof; current: NoticeProof };
type ReviewSection = 'changes' | 'pdfs';

/** Presents captured evidence. Source and proof authority stay with the editor owner. */
@Component({
  selector: 'phms-notice-change-review',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    UiButtonDirective,
    UiControlDirective,
    NoticeProofViewportDirective,
  ],
  templateUrl: './notice-change-review.component.html',
  styleUrl: './notice-change-review.component.scss',
})
export class NoticeChangeReviewComponent
  implements OnChanges, AfterViewChecked, OnDestroy
{
  @Input() baseline: SavedNoticeSource | null = null;
  @Input() composition: NoticeComposition | null = null;
  @Input() currentSourceHash: string | null = null;
  @Input() comparison: NoticeCompositionComparison | null = null;
  @Input() proofs: ProofPair | null = null;
  @Input() canCapture = false;
  @Input() canRender = false;
  @Input() canUndoBaseline = false;
  @Input() baselineDiagnostics: readonly NoticeDiagnostic[] = [];
  @Input() disabled = false;
  @Output() readonly captureBaseline = new EventEmitter<void>();
  @Output() readonly clearBaseline = new EventEmitter<void>();
  @Output() readonly undoBaseline = new EventEmitter<void>();
  @Output() readonly renderComparison = new EventEmitter<void>();
  @Output() readonly editBlock = new EventEmitter<string>();
  @Output() readonly downloadProof = new EventEmitter<ProofSide>();
  @Output() readonly fieldsRequested = new EventEmitter<void>();
  @ViewChild('reviewBody') private reviewBody?: ElementRef<HTMLElement>;
  @ViewChild('changesHeading') private changesHeading?: ElementRef<HTMLElement>;
  @ViewChild('pdfHeading') private pdfHeading?: ElementRef<HTMLElement>;

  section: ReviewSection = 'changes';
  pageNumber = 1;
  zoom: NoticeProofZoom = 'fit-page';
  readonly sides = ['baseline', 'current'] as const;
  private awaitingRenderedPair = false;
  private focusSection = false;
  private resetSectionScroll = false;
  private resetCanvasScroll = false;
  private pairIdentity: string | null = null;
  private viewports: Record<ProofSide, NoticeProofViewport | null> = {
    baseline: null,
    current: null,
  };
  private renderFocusElement: Element | null = null;
  private renderFocusMoved = false;
  private removeRenderFocusObserver: (() => void) | null = null;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['baseline'] || changes['currentSourceHash']) {
      this.pageNumber = 1;
      this.awaitingRenderedPair = false;
      this.clearRenderFocusObserver();
    }
    if (!this.baseline) {
      this.zoom = 'fit-page';
      this.section = 'changes';
    }
    const pair = this.exactProofs;
    const identity = pair
      ? JSON.stringify(
          this.sides.map((side) => [
            pair[side].sourceJson,
            pair[side].sourceHash,
            pair[side].exampleCode,
            pair[side].contextHash,
            pair[side].rendererCode,
            pair[side].rendererVersion,
          ]),
        )
      : null;
    if (identity !== this.pairIdentity) {
      this.pageNumber = 1;
      this.zoom = 'fit-page';
      this.resetCanvasScroll = true;
      this.pairIdentity = identity;
    }
    if (changes['proofs'] && this.awaitingRenderedPair && this.exactProofs) {
      this.awaitingRenderedPair = false;
      // A later keyboard task owns both its focus and the visible section.
      // Completed evidence remains discoverable through the Ready indicator.
      if (this.canFocusRenderedPair()) {
        this.section = 'pdfs';
        this.resetSectionScroll = true;
        this.focusSection = true;
      }
      this.clearRenderFocusObserver();
    }
    if (
      changes['disabled']?.previousValue === true &&
      !this.disabled &&
      this.awaitingRenderedPair &&
      !this.exactProofs
    ) {
      this.awaitingRenderedPair = false;
      this.clearRenderFocusObserver();
    }
    const page = Math.min(Math.max(1, this.pageNumber), this.pageCount || 1);
    if (page !== this.pageNumber) this.resetCanvasScroll = true;
    this.pageNumber = page;
  }

  ngAfterViewChecked(): void {
    if (this.resetCanvasScroll) {
      this.resetCanvasScroll = false;
      this.reviewBody?.nativeElement
        .querySelectorAll<HTMLElement>('.proof-canvas')
        .forEach((canvas) => {
          canvas.scrollTop = 0;
          canvas.scrollLeft = 0;
        });
    }
    if (this.resetSectionScroll) {
      this.resetSectionScroll = false;
      if (this.reviewBody) this.reviewBody.nativeElement.scrollTop = 0;
    }
    if (!this.focusSection) return;
    this.focusSection = false;
    const heading =
      this.section === 'changes' ? this.changesHeading : this.pdfHeading;
    heading?.nativeElement.focus({ preventScroll: true });
  }

  get changeCount(): number {
    return this.baseline && this.composition && this.comparison?.compatible
      ? this.comparison.changes.length
      : 0;
  }

  showSection(section: ReviewSection): void {
    if (section === 'pdfs' && !this.baseline) return;
    this.awaitingRenderedPair = false;
    this.clearRenderFocusObserver();
    this.section = section;
    this.resetSectionScroll = true;
    this.focusSection = true;
  }

  ngOnDestroy(): void {
    this.clearRenderFocusObserver();
  }

  private observeRenderFocus(): void {
    this.clearRenderFocusObserver();
    const document = this.reviewBody?.nativeElement.ownerDocument;
    if (!document) return;
    this.renderFocusElement = document.activeElement;
    this.renderFocusMoved = false;
    const observer = (event: FocusEvent): void => {
      if (
        event.target !== this.renderFocusElement &&
        event.target !== document.body &&
        event.target !== document.documentElement
      )
        this.renderFocusMoved = true;
    };
    document.addEventListener('focusin', observer, true);
    this.removeRenderFocusObserver = () =>
      document.removeEventListener('focusin', observer, true);
  }

  private canFocusRenderedPair(): boolean {
    const document = this.reviewBody?.nativeElement.ownerDocument;
    if (!document || this.renderFocusMoved) return false;
    const trigger = this.renderFocusElement;
    return (
      document.activeElement === trigger ||
      (document.activeElement === document.body &&
        (!trigger ||
          trigger === document.body ||
          !trigger.isConnected ||
          trigger.matches(':disabled')))
    );
  }

  private clearRenderFocusObserver(): void {
    this.removeRenderFocusObserver?.();
    this.removeRenderFocusObserver = null;
    this.renderFocusElement = null;
    this.renderFocusMoved = false;
  }

  get exactProofs(): ProofPair | null {
    const pair = this.proofs;
    const baseline = this.baseline;
    if (
      !pair ||
      !baseline ||
      !this.composition ||
      !this.comparison?.compatible ||
      !this.currentSourceHash ||
      pair.baseline.sourceHash !== baseline.sourceHash ||
      pair.baseline.sourceJson !== baseline.sourceJson ||
      pair.current.sourceHash !== this.currentSourceHash ||
      pair.baseline.contextHash !== pair.current.contextHash ||
      pair.baseline.exampleCode !== pair.current.exampleCode ||
      this.sides.some(
        (side) =>
          pair[side].rendererCode !== baseline.rendererCode ||
          pair[side].rendererVersion !== baseline.rendererVersion,
      )
    )
      return null;
    return pair;
  }

  get pageCount(): number {
    const pair = this.exactProofs;
    return pair ? Math.max(pair.baseline.pageCount, pair.current.pageCount) : 0;
  }

  get pageNumbers(): number[] {
    return Array.from({ length: this.pageCount }, (_, index) => index + 1);
  }

  get proofScale(): number | null {
    if (!this.exactProofs) return null;
    return computeNoticeProofScale(
      this.zoom,
      this.sides.flatMap((side) => {
        const page = this.proofPage(side);
        return page ? [{ ...page, viewport: this.viewports[side] }] : [];
      }),
    );
  }

  updateViewport(side: ProofSide, viewport: NoticeProofViewport | null): void {
    this.viewports[side] = viewport;
  }

  get groups(): string[] {
    return [
      ...new Set(
        (this.comparison?.changes ?? []).map((change) => change.group),
      ),
    ];
  }

  changesFor(group: string): NoticeCompositionComparison['changes'] {
    return (this.comparison?.changes ?? []).filter(
      (change) => change.group === group,
    );
  }

  proofPage(side: ProofSide): NoticeProof['pages'][number] | null {
    return (
      this.exactProofs?.[side].pages.find(
        (page) => page.number === this.pageNumber,
      ) ?? null
    );
  }

  canEditBlock(blockId: string | undefined): boolean {
    return (
      !!blockId &&
      !!this.composition?.blocks.some((block) => block.id === blockId)
    );
  }

  capture(): void {
    if (!this.disabled && this.canCapture) this.captureBaseline.emit();
  }

  clear(): void {
    if (!this.disabled && this.baseline) this.clearBaseline.emit();
  }

  undo(): void {
    if (!this.disabled && this.canUndoBaseline) this.undoBaseline.emit();
  }

  render(): void {
    if (
      !this.disabled &&
      this.canRender &&
      this.baseline &&
      this.composition &&
      this.comparison?.compatible
    ) {
      this.awaitingRenderedPair = true;
      this.observeRenderFocus();
      this.renderComparison.emit();
    }
  }

  edit(blockId: string): void {
    if (!this.disabled && this.canEditBlock(blockId))
      this.editBlock.emit(blockId);
  }

  fields(): void {
    if (!this.disabled) this.fieldsRequested.emit();
  }

  download(side: ProofSide): void {
    if (!this.disabled && this.exactProofs) this.downloadProof.emit(side);
  }

  setPage(value: number): void {
    const page = Number(value);
    if (
      Number.isInteger(page) &&
      page >= 1 &&
      page <= this.pageCount &&
      page !== this.pageNumber
    ) {
      this.pageNumber = page;
      this.resetCanvasScroll = true;
    }
  }

  setZoom(value: unknown): void {
    if (
      value === 'fit-page' ||
      value === 'fit-width' ||
      value === 50 ||
      value === 75 ||
      value === 100
    )
      this.zoom = value;
  }

  pageKeydown(event: KeyboardEvent): void {
    if (event.target !== event.currentTarget || !this.exactProofs) return;
    const requested =
      event.key === 'ArrowLeft'
        ? this.pageNumber - 1
        : event.key === 'ArrowRight'
          ? this.pageNumber + 1
          : event.key === 'Home'
            ? 1
            : event.key === 'End'
              ? this.pageCount
              : null;
    if (requested === null) return;
    event.preventDefault();
    this.setPage(Math.min(Math.max(1, requested), this.pageCount));
  }
}
